import { makeDeviceKeys } from "@bandstand/api/requestSignature";
import type { DeviceKeys } from "@bandstand/api/requestSignature";

/** The database that holds the device's key pair, apart from the library. */
const DATABASE = "bandstand-device";

const STORE = "keys";

let keys: Promise<DeviceKeys> | undefined;

/**
 * This device's key pair, which signs every request to the share server. It's made the first time, and kept in
 * IndexedDB, which can keep a private key without anyone being able to read it out.
 */
export function deviceKeys(): Promise<DeviceKeys> {
    keys ??= loadOrMake();

    return keys;
}

async function loadOrMake(): Promise<DeviceKeys> {
    const database = await open();
    const saved = await run<DeviceKeys | undefined>(database, "readonly", (store) => store.get("device"));

    if (saved) return saved;

    const made = await makeDeviceKeys();

    await run(database, "readwrite", (store) => store.put(made, "device"));

    return made;
}

function open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE, 1);

        request.onupgradeneeded = () => request.result.createObjectStore(STORE);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function run<T>(database: IDBDatabase, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest): Promise<T> {
    return new Promise((resolve, reject) => {
        const request = action(database.transaction(STORE, mode).objectStore(STORE));

        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
    });
}
